import { PMTiles, RangeResponse, Source } from "pmtiles"
import { RequestParameters } from "maplibre-gl"
import { IsOnline } from "./Web/IsOnline"
import Constants from "../Models/Constants"
import { Store, UIEventSource } from "./UIEventSource"
import { Utils } from "../Utils"

export interface AreaDescription {
    /**
     * Thie filename at the host and in the indexedDb
     * Host name is not included
     */
    name: string
    /**
     * Minzoom that is covered, inclusive
     */
    minzoom: number
    /**
     * Maxzoom that is covered, inclusive
     */
    maxzoom: number
    /**
     * The x, y of the tile that is covered (at minzoom)
     */
    x: number
    y: number

    /**
     * ISO-datestring of when the data was processed
     */
    dataVersion?: string

    /**
     * Blob.size
     */
    size?: number
}

class TypedIdb<T> {
    private readonly _db: Promise<IDBDatabase>
    private readonly _name: string

    constructor(db: string) {
        this._name = db
        this._db = TypedIdb.openDb(db)
    }

    private static openDb(name: string): Promise<IDBDatabase> {
        if (Utils.runningFromConsole) {
            return Promise.resolve(undefined)
        }
        return new Promise((resolve, reject) => {
            const request: IDBOpenDBRequest = indexedDB.open(name)
            request.onerror = (event) => {
                console.error("Could not open the Database: ", event)
                reject(event)
            }
            request.onupgradeneeded = () => {
                const db = request.result
                db.createObjectStore(name)
            }
            request.onsuccess = () => {
                resolve(request.result)
            }
        })
    }

    async set(key: string, value: T): Promise<void> {
        if (Utils.runningFromConsole) {
            return Promise.resolve()
        }
        const db = await this._db
        return new Promise((resolve, reject) => {
            const tx = db.transaction([this._name], "readwrite")
            const store = tx.objectStore(this._name)
            const request = store.put(value, key)

            request.onsuccess = () => resolve()
            request.onerror = () => reject(request.error)
        })
    }

    async get(key: string): Promise<T> {
        if (Utils.runningFromConsole) {
            return Promise.resolve(undefined)
        }
        const db = await this._db
        return new Promise((resolve, reject) => {
            const tx = db.transaction([this._name], "readonly")
            const store = tx.objectStore(this._name)
            const request: IDBRequest<T> = store.get(key)

            request.onsuccess = () => resolve(request.result)
            request.onerror = () => reject(request.error)
        })
    }

    async getAllValues(): Promise<T[]> {
        if (Utils.runningFromConsole) {
            return Promise.resolve([])
        }
        const db = await this._db
        return new Promise((resolve, reject) => {
            const tx = db.transaction([this._name], "readonly")
            const store = tx.objectStore(this._name)
            const values: T[] = []
            const request = store.openCursor()

            request.onsuccess = () => {
                const cursor = request.result
                if (cursor) {
                    values.push(cursor.value)
                    cursor.continue() // Triggers a new 'onsuccess' event
                } else {
                    resolve(values)
                }
            }

            request.onerror = () => reject(request.error)
        })
    }

    async del(key: string): Promise<void> {
        const db = await this._db
        return new Promise((resolve, reject) => {
            const tx = db.transaction([this._name], "readwrite")
            const store = tx.objectStore(this._name)
            const request = store.delete(key)

            request.onsuccess = () => resolve()
            request.onerror = () => reject(request.error)
        })
    }
}

class BlobSource implements Source {
    private readonly _blob: Blob
    private readonly _name: string
    public readonly pmtiles: PMTiles

    constructor(name: string, blob: Blob) {
        this._name = name
        this._blob = blob
        this.pmtiles = new PMTiles(this)
    }

    async getBytes(offset: number, length: number): Promise<RangeResponse> {
        const sliced = this._blob.slice(offset, offset + length)
        return {
            data: await sliced.arrayBuffer(),
        }
    }

    getKey() {
        return this._name
    }

    /**
     * The underlying data was created with an extract from OpenStreetMap,
     * extracted at a certain date. This date can be used as version indicator.
     */
    async getDataVersion(): Promise<string> {
        const meta: Record<string, string> = <any>await this.pmtiles.getMetadata()
        return meta["planetiler:osm:osmosisreplicationtime"]
    }
}

export class OfflineBasemapManager {
    /**
     * Where to get the initial map tiles
     * @private
     */
    private readonly _host: string

    public static readonly zoomelevels: Record<number, number | undefined> = {
        0: 4,
        5: 7,
        8: 9,
        10: undefined,
    } as const

    private readonly blobs: TypedIdb<any>
    private readonly meta: TypedIdb<AreaDescription>
    public _installedAreas: UIEventSource<AreaDescription[]> = new UIEventSource([])
    public installedAreas: Store<ReadonlyArray<Readonly<AreaDescription>>> = this._installedAreas

    private readonly _installing: UIEventSource<Map<string, Promise<boolean>>> = new UIEventSource(
        new Map()
    )
    public readonly installing: Store<ReadonlyMap<string, object>> = this._installing
    public static singleton = new OfflineBasemapManager(Constants.pmtiles_host)

    /**
     * The 'offline base map manager' is responsible for keeping track of the locally installed 'protomaps' subpyramids.
     *
     * Roughly taken, a 'protomaps pyramid' is a collection of MVT-tiles in a single file, all belonging to a certain bbox.
     * The 'cache.mapcomplete.org' server has a collection of sliced pyramids:
     *
     * global-basemap.pmtiles: all tiles from z=0 to z=4, ~15MB
     * 5-{x}-{y}.pmtiles: zoom levels 5 'till 8 (up to 9MB)
     * 9-{x}-{y}.pmtimes: zoom levels 8 till 15 for a region, up to 90MB
     *
     * When a user downloads an offline map, they download a 9-* subpyramid, the corresponding 5-8 pyramid and the 'global-basemap'
     *
     */
    private constructor(host: string) {
        if (!host.endsWith("/")) {
            host += "/"
        }
        this._host = host
        this.blobs = new TypedIdb("OfflineBasemap")
        this.meta = new TypedIdb<AreaDescription>("OfflineBasemapMeta")
        this.updateCachedMeta()
    }

    public async updateCachedMeta(): Promise<ReadonlyArray<Readonly<AreaDescription>>> {
        this._installedAreas.set(await this.meta.getAllValues())
        return this.installedAreas.data
    }

    public isInstalled(toCompare: { z?: number; minzoom?: number; x: number; y: number }): boolean {
        return this.installedAreas.data.some(
            (area) =>
                area.x === toCompare.x &&
                area.y === toCompare.y &&
                (toCompare.minzoom ?? toCompare.z) === area.minzoom
        )
    }

    /**
     * Returns all AreaDescriptions needed for the specified tile. Most specific zoom level last.
     * Already installed area descriptions are _not_ returned
     * @param tile
     */
    public *getInstallCandidates(tile: {
        z: number
        x: number
        y: number
    }): Generator<AreaDescription, void, unknown> {
        for (const k in OfflineBasemapManager.zoomelevels) {
            const z = Number(k)

            const zDiff = tile.z - z
            if (zDiff < 0) {
                continue
            }
            const x = tile.x >> zDiff
            const y = tile.y >> zDiff
            if (!this.isInstalled({ z, x, y })) {
                yield <AreaDescription>{
                    name: `${z}-${x}-${y}.pmtiles`,
                    minzoom: z,
                    maxzoom: OfflineBasemapManager.zoomelevels[z] ?? 15,
                    x,
                    y,
                }
            }
        }
    }

    /**
     * Installs the area if not yet installed
     * @param areaDescription
     * @private
     */
    public async installArea(areaDescription: AreaDescription) {
        const target = this._host + areaDescription.name
        if (this.isInstalled(areaDescription)) {
            // Already installed
            return true
        }
        console.log("Installing area from " + target)
        const response = await fetch(target)
        if (!response.ok) {
            return false
        }
        const blob = await response.blob()
        await this.blobs.set(areaDescription.name, blob)
        areaDescription.dataVersion = await new BlobSource(
            areaDescription.name,
            blob
        ).getDataVersion()
        areaDescription.size = blob.size
        await this.meta.set(areaDescription.name, areaDescription)
        await this.updateCachedMeta()
        console.log("Successfully installed", areaDescription.name)
        return true
    }

    /**
     * Knows about the cache structure of cache.mapcomplete.org
     * @see GeneratePmTilesExtractionScript
     */
    public static getAreaDescriptionForMapcomplete(name: string): AreaDescription {
        if (!name.endsWith(".pmtiles")) {
            throw "Invalid filename, should end with .pmtiles"
        }
        const [z, x, y] = name
            .substring(0, name.length - ".pmtiles".length)
            .split("-")
            .map(Number)
        const maxzooms: Record<number, number | undefined> = this.zoomelevels
        return {
            name,
            minzoom: z,
            maxzoom: maxzooms[z] ?? 15,
            x,
            y,
        }
    }

    /**
     * Looks through the 'installedMeta' where a tile can be extracted from
     * @param z
     * @param x
     * @param y
     * @private
     */
    private determineArea(z: number, x: number, y: number): AreaDescription | undefined {
        for (const areaDescription of this.installedAreas.data) {
            if (areaDescription.minzoom > z) {
                continue
            }
            if (areaDescription.maxzoom < z) {
                continue
            }
            const zdiff = z - areaDescription.minzoom
            const xAtMinz = x >> zdiff
            const yAtMinZ = y >> zdiff
            if (xAtMinz === areaDescription.x && yAtMinZ === areaDescription.y) {
                return areaDescription
            }
        }
        return undefined
    }

    /**
     * Attempts to install the given area,
     * returns 'true' if the area was successfully installed OR was already installed previously
     * @param candidate
     * @private
     */
    private attemptInstall(candidate: AreaDescription): Promise<boolean> {
        if (!IsOnline.isOnline.data) {
            return Promise.resolve(false)
        }
        const alreadyInstalling = this._installing.data.get(candidate.name)
        if (alreadyInstalling) {
            return alreadyInstalling
        }
        const promise = this.installArea(candidate)
            .catch((e) => {
                console.error("Could not install basemap archive", candidate.name, "due to", e)

                return false
            })
            .finally(() => {
                this._installing.data.delete(candidate.name)
                this._installing.ping()
            })
        this._installing.data.set(candidate.name, promise)
        this._installing.ping()
        return promise
    }

    /**
     * Attempts to install all required areas for the given location
     * @param tile
     */
    public async autoInstall(tile: { z: number; x: number; y: number }) {
        const candidates = this.getInstallCandidates(tile)
        for (const candidate of candidates) {
            await this.attemptInstall(candidate)
        }
    }

    /**
     *
     * Searches for the correct MVT tile locally and returns it as a response.
     * Returns undefined if not found
     * @param z
     * @param x
     * @param y
     */
    async getTileResponse(z: number, x: number, y: number): Promise<undefined | Response> {
        if (this._installedAreas.data.length === 0) {
            await this.updateCachedMeta()
        }
        const area = this.determineArea(z, x, y)
        if (!area) {
            console.log("No suitable area in the archives (and no fallback):", { z, x, y })
            return undefined
        }
        const blob = await this.blobs.get(area.name)
        const pmtiles = new BlobSource(area.name, blob)
        const tileData = await pmtiles.pmtiles.getZxy(z, x, y)
        if (!tileData) {
            console.log("Not found in the archives:", { z, x, y })
            return undefined
        }
        return new Response(tileData.data, {
            headers: { "Content-Type": "application/x.protobuf" },
        })
    }

    deleteArea(description: AreaDescription): Promise<ReadonlyArray<Readonly<AreaDescription>>> {
        this.blobs.del(description.name)
        this.meta.del(description.name)
        return this.updateCachedMeta()
    }

    private async fallback(params: RequestParameters, abortController: AbortController) {
        params.url = params.url.substr("pmtilesoffl://".length)
        const response = await fetch(new Request(params.url, params), abortController)
        if (!response.ok) {
            throw new Error("Could not fetch " + params.url + "; status code is" + response.status)
        }
        return { data: await response.arrayBuffer() }
    }

    public async tilev4(
        params: RequestParameters,
        abortController: AbortController
    ): Promise<
        | { data: unknown }
        | { data: { tiles: string[]; minzoom: number; maxzoom: number; bounds: number[] } }
        | {
              data: Uint8Array
              cacheControl: string
              expires: string
          }
        | { data: Uint8Array }
        | { data: null }
    > {
        if (params.type === "arrayBuffer") {
            const re = new RegExp(/(\d+)\/(\d+)\/(\d+).(mvt|pbf)/)
            const result = params.url.match(re)
            if (!result) {
                return await this.fallback(params, abortController)
            }
            const z = Number(result[1])
            const x = Number(result[2])
            const y = Number(result[3])
            const r = await this.getTileResponse(z, x, y)
            if (r?.ok) {
                return { data: await r.arrayBuffer() }
            }
        }
        return await this.fallback(params, abortController)
    }
}
